import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRight, Users, HeartHandshake, Building, Cpu, Clock, Briefcase, Lightbulb } from 'lucide-react';
import { useResponsiveAnimation } from '../hooks/useResponsiveAnimation';
import { useForceAnimation } from '../hooks/useForceAnimation';

const Portfolio = () => {
  const { duration, baseDelay, staggerDelay } = useResponsiveAnimation();

  const [ref, inView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
    rootMargin: '0px 0px -10% 0px',
  });

  const isVisible = useForceAnimation(inView);

  useEffect(() => {
    console.log(`Portfolio section visibility: ${isVisible ? 'visible' : 'hidden'}`);
  }, [isVisible]);

  const sellerOpportunities = [
    {
      title: 'AI SaaS Startups',
      category: 'AI Software',
      description:
        'We are interested in AI-related SaaS startups with a clear product, real user need, and an opportunity to strengthen execution after acquisition or partnership.',
      image: '/people-working-out-their-sales-targets.jpeg',
      icon: <Users size={20} className="text-primary-600" />,
    },
    {
      title: 'Established SaaS Products',
      category: 'Recurring Revenue',
      description:
        'We look for software businesses with repeatable demand, defensible use cases, and product depth that can support durable ownership.',
      image: '/happy-about-the-quarterly-earnings-report.jpeg',
      icon: <Building size={20} className="text-primary-600" />,
    },
    {
      title: 'Digital Infrastructure Businesses',
      category: 'Online Operations',
      description:
        'We are open to internet-native businesses with strong operational workflows, proprietary processes, or software-backed service delivery.',
      image: '/Comming-up-with-new-sales-targets.jpeg',
      icon: <Cpu size={20} className="text-primary-600" />,
    },
    {
      title: 'Founder-Led Online Businesses',
      category: 'Transition',
      description:
        'If a founder wants to explore acquisition, transition, or strategic involvement, we are open to that conversation.',
      image: '/find-new-revenue-source-beyond-the-usual.jpeg',
      icon: <HeartHandshake size={20} className="text-primary-600" />,
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: staggerDelay * 2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration },
    },
  };

  return (
    <section id="portfolio" className="py-20 mt-16 relative z-10 bg-white">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <motion.span
            initial={{ opacity: 0 }}
            animate={isVisible ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration }}
            className="inline-block py-1 px-3 rounded-full bg-primary-100 text-primary-600 text-sm font-medium mb-4"
          >
            Focus Areas
          </motion.span>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration, delay: baseDelay / 2 }}
            className="text-3xl md:text-4xl font-bold mb-6"
          >
            Digital Businesses We Are Interested In
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration, delay: baseDelay }}
            className="text-lg text-gray-600 leading-relaxed"
          >
            Our interest is centered on AI-related SaaS startups, software products, and online businesses where
            acquisition or long-term ownership can create a stronger company.
          </motion.p>
        </div>

        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? 'visible' : 'hidden'}
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
        >
          {sellerOpportunities.map((item, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
              className="group relative bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 flex flex-col h-full"
            >
              <div className="h-48 overflow-hidden relative">
                <img src={item.image} alt={item.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center mr-3">{item.icon}</div>
                    <span className="text-white font-medium">{item.category}</span>
                  </div>
                </div>
              </div>
              <div className="p-6 flex-grow">
                <h3 className="text-xl font-bold mb-3 group-hover:text-primary-600 transition-colors">{item.title}</h3>
                <p className="text-gray-600 mb-4">{item.description}</p>
              </div>
              <div className="p-6 pt-0">
                <a href="#contact" className="inline-flex items-center text-primary-600 font-medium hover:text-primary-700 transition-colors">
                  <span>Learn more</span>
                  <ArrowRight size={16} className="ml-2" />
                </a>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration, delay: baseDelay * 1.5 }}
          className="mt-16 bg-gray-50 rounded-2xl p-8 md:p-12 max-w-5xl mx-auto"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center md:text-left md:col-span-2">
              <h3 className="text-2xl font-bold mb-4">Thinking About a Transition?</h3>
              <p className="text-gray-600 mb-6">
                If you are exploring an acquisition, founder transition, or AI SaaS venture conversation, we prefer
                direct discussions grounded in the actual product and business.
              </p>
              <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                <div className="flex items-center">
                  <Clock size={18} className="text-primary-600 mr-2" />
                  <span className="text-sm">Confidential outreach</span>
                </div>
                <div className="flex items-center">
                  <Briefcase size={18} className="text-primary-600 mr-2" />
                  <span className="text-sm">Operator-aware diligence</span>
                </div>
                <div className="flex items-center">
                  <Lightbulb size={18} className="text-primary-600 mr-2" />
                  <span className="text-sm">Clear next steps</span>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href="#contact"
                className="inline-flex items-center justify-center px-6 py-3 rounded-md bg-primary-600 text-white font-medium transition-colors hover:bg-primary-700 w-full md:w-auto"
              >
                Start the Conversation
              </motion.a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Portfolio;
